import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Search, Edit2, UserX, UserCheck, Copy, AlertTriangle, Shield } from 'lucide-react';
import apiService from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';

const ROLE_OPTIONS = [
  { value: 'MANAGER', label: 'Manager', description: 'Accès étendu : produits, stock, promotions, analytics, remboursements' },
  { value: 'VENDEUR', label: 'Vendeur', description: 'Vente, commandes et consultation' },
  { value: 'CAISSIER', label: 'Caissier', description: 'Caisse uniquement' },
];

const ROLE_COLORS = {
  GERANT: 'bg-purple-100 text-purple-800',
  MANAGER: 'bg-blue-100 text-blue-800',
  VENDEUR: 'bg-green-100 text-green-800',
  CAISSIER: 'bg-orange-100 text-orange-800',
};

const ROLE_LABELS = {
  GERANT: 'Gérant',
  MANAGER: 'Manager',
  VENDEUR: 'Vendeur',
  CAISSIER: 'Caissier',
};

const STATUT_COLORS = {
  ACTIF: 'bg-green-100 text-green-800',
  INACTIF: 'bg-gray-100 text-gray-800',
};

const initialFormData = {
  email: '',
  nom: '',
  prenom: '',
  telephone: '',
  roleInterne: 'VENDEUR',
};

const Staff = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [createdUserName, setCreatedUserName] = useState('');
  const [passwordCopied, setPasswordCopied] = useState(false);

  const [confirmAction, setConfirmAction] = useState(null);

  const { toasts, toast, removeToast } = useToast();

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getStaff();
      setStaff(data.staff || []);
    } catch (err) {
      setError(err.data?.error?.message || 'Erreur lors du chargement de l\'équipe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  const filteredStaff = staff.filter((member) => {
    if (filterRole && member.roleInterne !== filterRole) return false;
    if (filterStatut && member.statut !== filterStatut) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        member.nom?.toLowerCase().includes(term) ||
        member.prenom?.toLowerCase().includes(term) ||
        member.email?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (member) => {
    setEditingUser(member);
    setFormData({
      email: member.email,
      nom: member.nom || '',
      prenom: member.prenom || '',
      telephone: member.telephone || '',
      roleInterne: member.roleInterne,
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData(initialFormData);
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      if (editingUser) {
        const payload = {};
        if (formData.nom !== editingUser.nom) payload.nom = formData.nom;
        if (formData.prenom !== editingUser.prenom) payload.prenom = formData.prenom;
        if (formData.telephone !== (editingUser.telephone || '')) payload.telephone = formData.telephone;
        if (formData.roleInterne !== editingUser.roleInterne) payload.roleInterne = formData.roleInterne;

        if (Object.keys(payload).length === 0) {
          closeModal();
          return;
        }

        await apiService.updateStaff(editingUser.id, payload);
        toast.success(`${formData.prenom} ${formData.nom} mis à jour`);
        closeModal();
        fetchStaff();
      } else {
        const payload = {
          email: formData.email.trim(),
          nom: formData.nom.trim(),
          prenom: formData.prenom.trim(),
          roleInterne: formData.roleInterne,
        };
        if (formData.telephone.trim()) {
          payload.telephone = formData.telephone.trim();
        }

        const result = await apiService.createStaff(payload);

        closeModal();

        if (result.password) {
          setTempPassword(result.password);
          setCreatedUserName(`${payload.prenom} ${payload.nom}`);
          setPasswordCopied(false);
          setShowPasswordModal(true);
        } else {
          toast.success(`${payload.prenom} ${payload.nom} ajouté à l'équipe`);
        }

        fetchStaff();
      }
    } catch (err) {
      const msg = err.data?.error?.message || err.message || 'Erreur lors de l\'opération';
      setFormError(msg);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivate = (member) => {
    setConfirmAction({
      type: 'deactivate',
      member,
      title: 'Désactiver cet employé',
      description: `${member.prenom} ${member.nom} ne pourra plus se connecter. Ses données historiques seront conservées.`,
      confirmLabel: 'Désactiver',
      tone: 'danger',
      action: async () => {
        await apiService.deactivateStaff(member.id);
        toast.success(`${member.prenom} ${member.nom} désactivé`);
        fetchStaff();
      },
    });
  };

  const handleActivate = (member) => {
    setConfirmAction({
      type: 'activate',
      member,
      title: 'Réactiver cet employé',
      description: `${member.prenom} ${member.nom} pourra à nouveau se connecter.`,
      confirmLabel: 'Réactiver',
      tone: 'primary',
      action: async () => {
        await apiService.activateStaff(member.id);
        toast.success(`${member.prenom} ${member.nom} réactivé`);
        fetchStaff();
      },
    });
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(tempPassword);
      setPasswordCopied(true);
      setTimeout(() => setPasswordCopied(false), 2000);
    } catch {
      toast.error('Impossible de copier le mot de passe');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mon Équipe</h1>
            <p className="text-gray-500 mt-1">Gérez les employés de votre commerce</p>
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error && staff.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon Équipe</h1>
          <p className="text-red-600 mt-2">{error}</p>
        </div>
        <button
          onClick={fetchStaff}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toasts */}
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onClose={() => removeToast(t.id)} />
      ))}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon Équipe</h1>
          <p className="text-gray-500 mt-1">
            {staff.length} employé{staff.length !== 1 ? 's' : ''} au total
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium text-sm"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouvel employé
        </button>
      </div>

      {/* Filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un employé..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
        </div>
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Tous les rôles</option>
          <option value="GERANT">Gérant</option>
          {ROLE_OPTIONS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
        >
          <option value="">Tous les statuts</option>
          <option value="ACTIF">Actif</option>
          <option value="INACTIF">Inactif</option>
        </select>
      </div>

      {/* Tableau */}
      {filteredStaff.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          {staff.length === 0 ? (
            <>
              <p className="text-gray-500 mb-4">Aucun employé pour le moment</p>
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm font-medium"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter votre premier employé
              </button>
            </>
          ) : (
            <p className="text-gray-500">Aucun résultat pour ces filtres</p>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employé</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStaff.map((member) => (
                  <tr key={member.id} className={member.statut === 'INACTIF' ? 'opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-9 w-9 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-700 font-medium text-sm">
                            {(member.prenom?.[0] || '').toUpperCase()}{(member.nom?.[0] || '').toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {member.prenom} {member.nom}
                            {member.isSelf && (
                              <span className="ml-2 text-xs text-primary-600 font-normal">(vous)</span>
                            )}
                          </div>
                          {member.telephone && (
                            <div className="text-xs text-gray-500">{member.telephone}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {member.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_COLORS[member.roleInterne] || 'bg-gray-100 text-gray-800'}`}>
                        {member.roleInterne === 'GERANT' && <Shield className="h-3 w-3 mr-1" />}
                        {ROLE_LABELS[member.roleInterne] || member.roleInterne}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUT_COLORS[member.statut] || 'bg-gray-100 text-gray-800'}`}>
                        {member.statut === 'ACTIF' ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      {member.isSelf ? (
                        <span className="text-xs text-gray-400">-</span>
                      ) : (
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(member)}
                            className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition"
                            title="Modifier"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          {member.statut === 'ACTIF' ? (
                            <button
                              onClick={() => handleDeactivate(member)}
                              className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Désactiver"
                            >
                              <UserX className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(member)}
                              className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-lg transition"
                              title="Réactiver"
                            >
                              <UserCheck className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal création / modification */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title={editingUser ? 'Modifier un employé' : 'Nouvel employé'}
        maxWidth="max-w-lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {formError}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              required
              disabled={!!editingUser}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent ${editingUser ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
              placeholder="employe@email.com"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                required
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Dupont"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
              <input
                type="text"
                required
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Marie"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="06 12 34 56 78"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rôle *</label>
            <select
              required
              value={formData.roleInterne}
              onChange={(e) => setFormData({ ...formData, roleInterne: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <p className="mt-1.5 text-xs text-gray-500">
              {ROLE_OPTIONS.find((r) => r.value === formData.roleInterne)?.description}
            </p>
          </div>

          {!editingUser && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-700">
                Un mot de passe temporaire sera généré automatiquement. Vous devrez le transmettre à l'employé.
              </p>
            </div>
          )}

          <div className="flex items-center justify-end space-x-2 pt-2">
            <button
              type="button"
              onClick={closeModal}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={formLoading}
              className="px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition disabled:opacity-50"
            >
              {formLoading ? 'Chargement...' : editingUser ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal mot de passe temporaire */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Mot de passe temporaire"
        maxWidth="max-w-md"
        closeOnBackdrop={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            <strong>{createdUserName}</strong> a été ajouté à votre équipe.
          </p>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Ce mot de passe ne sera affiché qu'une seule fois
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Notez-le et transmettez-le à l'employé de manière sécurisée.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <code className="flex-1 px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg text-lg font-mono text-center tracking-wider select-all">
              {tempPassword}
            </code>
            <button
              onClick={copyPassword}
              className={`p-3 rounded-lg transition ${passwordCopied ? 'bg-green-100 text-green-700' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
              title="Copier"
            >
              <Copy className="h-5 w-5" />
            </button>
          </div>
          {passwordCopied && (
            <p className="text-xs text-green-600 text-center">Copié dans le presse-papiers</p>
          )}

          <button
            onClick={() => setShowPasswordModal(false)}
            className="w-full px-4 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition"
          >
            J'ai noté le mot de passe
          </button>
        </div>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmAction?.title}
        description={confirmAction?.description}
        confirmLabel={confirmAction?.confirmLabel}
        tone={confirmAction?.tone}
        onConfirm={async () => {
          try {
            await confirmAction?.action();
          } catch (err) {
            toast.error(err.data?.error?.message || 'Erreur lors de l\'opération');
            throw err;
          }
        }}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
};

export default Staff;
